/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef, useState } from 'react'

type SpeechToTextOptions = {
  getBaseText?(): string
  lang?: string
  onTextChange?(text: string): void
}

type UseSpeechToTextResult = {
  isListening: boolean
  isSupported: boolean
  start(): void
  stop(): void
  toggle(): void
}

export const canUseSpeechToText = (): boolean =>
  Boolean((window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition)

export const useSpeechToText = ({
  getBaseText,
  lang,
  onTextChange,
}: SpeechToTextOptions): UseSpeechToTextResult => {
  const [isListening, setIsListening] = useState(false)
  const recognitionRef = useRef<any | null>(null)
  const isListeningRef = useRef<boolean>(false)
  const speechBaseTextRef = useRef('')

  useEffect(() => {
    if (typeof window === 'undefined') return

    const SpeechRecognition =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition

    if (!SpeechRecognition) return

    const recognition = new SpeechRecognition()
    recognition.lang = lang
    recognition.continuous = true
    recognition.interimResults = true

    recognition.onstart = () => setIsListening(true)
    recognition.onend = () => setIsListening(false)
    recognition.onerror = () => setIsListening(false)

    recognition.onresult = (event: any) => {
      let transcript = ''

      for (let i = 0; i < event.results.length; i += 1) {
        transcript += event.results[i][0].transcript
      }

      const nextText = `${speechBaseTextRef.current} ${transcript}`.trim()

      if (isListeningRef.current) onTextChange?.(nextText)
    }

    recognitionRef.current = recognition

    return () => {
      recognition.stop()
      recognitionRef.current = null
    }
  }, [lang, onTextChange])

  const start = useCallback(() => {
    if (!recognitionRef.current) return

    isListeningRef.current = true
    speechBaseTextRef.current = getBaseText?.().trim() ?? ''
    recognitionRef.current.start()
  }, [getBaseText])

  const stop = useCallback(() => {
    isListeningRef.current = false
    recognitionRef.current?.stop()
  }, [])

  const toggle = useCallback(() => {
    if (isListening) {
      stop()

      return
    }

    start()
  }, [isListening, start, stop])

  return {
    isListening,
    isSupported: canUseSpeechToText(),
    start,
    stop,
    toggle,
  }
}
